import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col } from 'react-grid-system';
import { useDebounce } from 'use-debounce';

import { Header2 } from '../../../components/Typography';
import RepositList from '../../../components/Reposit/RepositList';
import Pagination from '../../../components/Pagination';
import Loading from '../../../components/Loading';
import Error from '../../../components/Error';
import { ContentContainer } from '../../../components/Common';
import Search from '../../../components/Search';
import Sort from '../../../components/Sort';
import Button from '../../../components/Button';
import {
  fetchRepositsRequested,
  updateFilters as updateFiltersActionCreator,
  Page,
  goToPage,
  FETCH_REPOSITS_STORE_KEY,
  BULK_IMPORT_REPOSITS_STORE_KEY,
} from '../../../redux/reposit-list/reposit-list.actions';
import { bulkImportRepositsThunk } from '../../../redux/reposit-list/reposit-list.thunk';
import {
  getRepositList,
  getRepositListFilters,
  getRepositListPagination,
} from '../../../redux/reposit-list/reposit-list.selectors';
import { RepositListFilters } from '../../../redux/reposit-list/reposit-list.types';
import {
  REPOSIT_FILTER_OPTIONS,
  RepositSort,
  LetOnlyFilter,
  getRepositFilterUpdate,
} from '../../../constants/reposit';
import { createLoadingSelector } from '../../../redux/loading/loading.selector';
import NoRepositsList from '../../../components/Reposit/NoRepositsList';
import { createErrorMessageSelector } from '../../../redux/error/error.selector';
import SelectFilter from '../../../components/SelectFilter';
import { AppState } from '../../../redux/root.reducer';
import { hasInternalRole } from '../../../redux/account/account.selectors';
import { UserDTOInternalRolesEnum } from '@reposit/api-client';
import { searchPageDocumentTitle, useAdminDocumentTitle } from '../../../hooks/use-document-title';

interface RepositListContainerProps {
  organizationId?: string;
}

const RepositListContainer: React.FC<RepositListContainerProps> = ({ organizationId }) => {
  const dispatch = useDispatch();

  const canBulkImport = useSelector((state: AppState) =>
    hasInternalRole(state, UserDTOInternalRolesEnum.BULKIMPORT)
  );

  const repositListLoadingSelector = createLoadingSelector([FETCH_REPOSITS_STORE_KEY]);
  const bulkImportLoadingSelector = createLoadingSelector([BULK_IMPORT_REPOSITS_STORE_KEY]);
  const getRepositListError = createErrorMessageSelector([FETCH_REPOSITS_STORE_KEY]);

  const fetchReposits = useCallback(() => dispatch(fetchRepositsRequested(organizationId)), [dispatch, organizationId]);
  const repositsGotoPage = useCallback((page: Page) => dispatch(goToPage(page)), [dispatch]);
  const isRepositListFetching = useSelector(repositListLoadingSelector);
  const isBulkImportLoading = useSelector(bulkImportLoadingSelector);
  const error = useSelector(getRepositListError);
  
  const handleBulkImport = useCallback(() => {
    dispatch(bulkImportRepositsThunk());
  }, [dispatch]);

  const { sort, query, filter, letOnly } = useSelector(getRepositListFilters);
  useAdminDocumentTitle(organizationId ? undefined : searchPageDocumentTitle('Reposits', query));
  const [debouncedQuery] = useDebounce(query, 800);
  const { page, count } = useSelector(getRepositListPagination);
  const updateFilters = useCallback(
    (filters: Partial<RepositListFilters>) => dispatch(updateFiltersActionCreator(filters)),
    [dispatch]
  );

  const reposits = useSelector(getRepositList);

  useEffect(() => {
    fetchReposits();
  }, [fetchReposits, debouncedQuery, sort, filter, page, letOnly]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      updateFilters({ query, includeDeleted: true, onlyDeleted: false });
    },
    [query, updateFilters]
  );

  const setFilter = (filteredValue: string) => {
    const filter = REPOSIT_FILTER_OPTIONS.find((f) => f.value === filteredValue);
    if (filter) {
      updateFilters(getRepositFilterUpdate(filter));
    }
  };

  const renderReposits = () => {
    if (error) return <Error error={error} />;
    if (isRepositListFetching) return <Loading />;
    if (!isRepositListFetching && !reposits.length) return <NoRepositsList currentFilter={filter} />;

    return (
      <>
        <RepositList reposits={reposits} />
        <Pagination count={count} currentPage={page} onNavigate={repositsGotoPage} />
      </>
    );
  };

  return (
    <Container>
      <Row>
        <Col sm={12}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Header2>Reposits</Header2>
            {canBulkImport && (
              <Button onClick={handleBulkImport} disabled={isBulkImportLoading}>
                Bulk Import
              </Button>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <ContentContainer>
            <form onSubmit={handleSubmit}>
              <Search onChange={(query) => updateFilters({ query })} value={query} placeholder="Search Reposits" />
            </form>
          </ContentContainer>
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <SelectFilter label="Filter by status" onSelect={(filter) => setFilter(filter)} options={REPOSIT_FILTER_OPTIONS} selected={filter} />
        </Col>
        <Col lg={4}>
          <SelectFilter
            label="Let Only"
            onSelect={(letOnly: LetOnlyFilter) => updateFilters({ letOnly })}
            options={[
              { value: LetOnlyFilter.ALL, label: 'All' },
              { value: LetOnlyFilter.YES, label: 'Yes' },
              { value: LetOnlyFilter.NO, label: 'No' },
            ]}
            selected={letOnly}
          />
        </Col>
        <Col lg={4} style={{ paddingBottom: 12 }}>
          <Sort
            label="Sort by"
            onSelect={(sort: RepositSort) => updateFilters({ sort })}
            options={[
              { label: 'End Date - Descending', value: RepositSort.END_DATE_DESCENDING },
              { label: 'End Date - Ascending', value: RepositSort.END_DATE_ASCENDING },
              { label: 'Start Date - Descending', value: RepositSort.START_DATE_DESCENDING },
              { label: 'Start Date - Ascending', value: RepositSort.START_DATE_ASCENDING },
              { label: 'Created At - Descending', value: RepositSort.CREATED_AT_DESCENDING },
              { label: 'Created At - Ascending', value: RepositSort.CREATED_AT_ASCENDING },
            ]}
            selected={sort}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={12}>{renderReposits()}</Col>
      </Row>
    </Container>
  );
};

export default RepositListContainer;
